<!-- Shared fragments. Icons are 14px strokes sized by .da-icon; the donor
     identity (avatar or initials, name, location) is one template used by
     the result rows and the confirm card; the DonorAtlas mark takes its
     size class (default .da-mark) and alt text from context — the toolbar
     copy is decorative, its button label names it, so it passes alt: ''. -->
<ng-template #daMark let-cls="cls" let-alt="alt">
    <img src="assets/logo_blue.png" [class]="cls || 'da-mark'" [alt]="alt ?? 'DonorAtlas'" />
</ng-template>
<ng-template #externalLinkIcon>
    <svg class="da-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path
            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25">
        </path>
    </svg>
</ng-template>
<ng-template #resyncIcon>
    <svg class="da-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="23 4 23 10 17 10"></polyline>
        <polyline points="1 20 1 14 7 14"></polyline>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
</ng-template>
<ng-template #donorIdentity let-donor>
    <img *ngIf="hasPhoto(donor)" class="da-avatar" [src]="donor.photo_url" alt=""
        (error)="onPhotoError(donor)" />
    <span *ngIf="!hasPhoto(donor)" class="da-avatar da-avatar-fallback">{{ initialsFor(donor.name) }}</span>
    <span class="da-result-text">
        <span class="da-result-name">{{ donor.name }}</span>
        <span *ngIf="donor.location" class="da-result-loc">{{ donor.location }}</span>
    </span>
</ng-template>

<!-- Tile root.
     When a donor is synced, padding drops to 0 so the toolbar + meta + iframe
     can run edge-to-edge. Non-synced states keep their padded, centered layout. -->
<div class="donoratlas-tile" [class.donoratlas-tile-synced]="!!summary?.profile_url">
    <!-- Initial load: spinner alone, before we know the state. Processing
         has its own spinner inside its state block below. -->
    <div class="donoratlas-header" *ngIf="loading">
        <app-spinning-logo [size]="24"></app-spinning-logo>
    </div>

    <!-- Error Alert -->
    <div *ngIf="errorMessage" class="da-alert" role="alert">
        <svg class="da-alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
            </path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <span>{{ errorMessage }}</span>
    </div>

    <!-- Main Content -->
    <ng-container *ngIf="!loading && summary">
        <!-- Not authorized - show login prompt -->
        <div *ngIf="!summary.authorized" class="da-state">
            <ng-container *ngTemplateOutlet="daMark"></ng-container>
            <p class="da-state-title">Log in to continue with DonorAtlas</p>
            <button class="da-btn da-btn-primary" type="button" (click)="onConfigureClick()">
                Log in
            </button>
        </div>

        <!-- Authorized but not configured - show setup prompt -->
        <div *ngIf="summary.authorized && !summary.configured" class="da-state">
            <ng-container *ngTemplateOutlet="daMark"></ng-container>
            <p class="da-state-title">Integration not configured</p>
            <p class="da-state-sub">Finish setup in DonorAtlas to connect this environment.</p>
            <a class="da-btn da-btn-secondary" [href]="setupUrl" target="_blank" rel="noopener noreferrer">
                Open setup
                <ng-container *ngTemplateOutlet="externalLinkIcon"></ng-container>
            </a>
        </div>

        <!-- =====================================================================
             Synced state: the profile IS the tile.
             Branded toolbar across the top, tiny meta line under it, and the
             donor profile iframe filling the rest at fixed height. No "Show
             Profile" toggle: the iframe is always visible when synced.
             ===================================================================== -->
        <ng-container *ngIf="summary.authorized && summary.configured && summary.profile_url">
            <div class="donoratlas-toolbar">
                <!-- Left: quiet "last synced" footnote plus the verb of any job
                     running against the record. The verb must not depend on
                     the stamp: a freshly registered sync (row created, items
                     still writing) has no last_synced_at yet, which is exactly
                     when the label matters most. -->
                <div class="donoratlas-toolbar-meta" *ngIf="syncStamp || processingVerbLabel">
                    <ng-container *ngIf="syncStamp">{{ syncStamp }}</ng-container>
                    <span *ngIf="processingVerbLabel" class="donoratlas-toolbar-meta-progress"><span
                            *ngIf="syncStamp"
                            class="donoratlas-toolbar-meta-progress-divider">·</span>{{ processingVerbLabel }}</span>
                </div>
                <!-- Spacer keeps actions right-aligned when there's no meta line. -->
                <div *ngIf="!syncStamp && !processingVerbLabel" class="donoratlas-toolbar-meta-spacer"></div>
                <div class="donoratlas-toolbar-actions">
                    <!-- Team-granted permissions decide which actions render
                         (the backend enforces the same set); a user with
                         neither sees a view-only note instead of empty space. -->
                    <span *ngIf="quotaExhausted" class="donoratlas-toolbar-note"
                        [title]="quotaTitle">{{ quotaTitle }}</span>
                    <span *ngIf="!quotaExhausted && !canSync && !canUnsync" class="donoratlas-toolbar-note"
                        [title]="permissionHint">View only</span>
                    <button *ngIf="canSync && !quotaExhausted" class="donoratlas-toolbar-btn" type="button" (click)="onResyncClick()"
                        [disabled]="mutationInFlight" title="Resync this constituent">
                        <ng-container *ngTemplateOutlet="resyncIcon"></ng-container>
                        <span>{{ enhancing ? 'Resyncing…' : 'Resync' }}</span>
                    </button>
                    <button *ngIf="canUnsync && !quotaExhausted" class="donoratlas-toolbar-btn" type="button" (click)="onUnsyncClick()"
                        [disabled]="mutationInFlight" title="Remove from DonorAtlas">
                        <svg class="da-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        <span>{{ unsyncing ? 'Unsyncing…' : 'Unsync' }}</span>
                    </button>
                    <!-- Open in DonorAtlas is a quiet "leave this view" link,
                         not a primary action like Resync/Unsync. The DA logo
                         leads the link so the brand mark rides with it,
                         then the label, then the external-link icon. -->
                    <button class="donoratlas-toolbar-link" type="button"
                        (click)="onViewProfileClick()" title="Open the full DonorAtlas profile">
                        <ng-container
                            *ngTemplateOutlet="daMark; context: { cls: 'donoratlas-toolbar-link-logo', alt: '' }"></ng-container>
                        <span>Open in DonorAtlas</span>
                        <ng-container *ngTemplateOutlet="externalLinkIcon"></ng-container>
                    </button>
                </div>
            </div>

            <!-- Profile frame.
                 profileLoading is set to true as soon as we know the donor
                 is synced (in loadProfile, kicked off by applySummary), so
                 the spinner shows from the moment the synced layout
                 appears. The iframe is mounted as soon as the URL arrives
                 and stays display:none under the spinner until the
                 embedded page sends donoratlas:loaded (or the 15s fallback
                 fires). -->
            <div class="donoratlas-profile-frame">
                <div *ngIf="profileLoading && !profileError" class="profile-loading">
                    <app-spinning-logo [size]="40"></app-spinning-logo>
                    <span class="profile-loading-text">Loading profile...</span>
                </div>
                <div *ngIf="profileError" class="profile-error">
                    <span>{{ profileError }}</span>
                    <button class="retry-btn" (click)="retryProfile()">Retry</button>
                </div>
                <iframe *ngIf="safeEmbedUrl && !profileError" class="donoratlas-profile-iframe"
                    [src]="safeEmbedUrl" [style.display]="profileLoading ? 'none' : 'block'"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    referrerpolicy="no-referrer"></iframe>
            </div>
        </ng-container>

        <!-- =====================================================================
             Configured but no synced profile: not-synced-yet, no-match-found,
             and processing.
             ===================================================================== -->
        <div *ngIf="summary.authorized && summary.configured && !summary.profile_url" class="da-body"
            [class.da-body-compact]="isOrgPage()">

            <!-- Organization pages: one compact row. Nothing to sync, but a
                 strong name match links to the org's DonorAtlas entity page. -->
            <div *ngIf="isOrgPage()" class="da-org">
                <!-- The DonorAtlas mark always leads; the entity's own logo
                     joins the title row when we matched one. -->
                <ng-container *ngTemplateOutlet="daMark; context: { cls: 'da-mark-sm' }"></ng-container>
                <div class="da-org-body">
                    <div class="da-org-titlerow">
                        <img *ngIf="summary.org_entity_logo_url && !orgLogoFailed"
                            class="da-org-logo" [src]="summary.org_entity_logo_url"
                            (error)="orgLogoFailed = true" alt="" />
                        <p class="da-org-title">{{ summary.constituent_name || 'Organization record'
                            }}</p>
                        <span class="da-pill da-org-badge">{{ summary.org_entity_type ||
                            'Organization' }}</span>
                    </div>
                    <p class="da-org-text">DonorAtlas syncs individual donors, so there's nothing to
                        sync for an organization record.</p>
                    <a *ngIf="summary.org_entity_url" class="da-org-link"
                        [href]="summary.org_entity_url" target="_blank" rel="noopener noreferrer"
                        [title]="summary.org_entity_name || ''">
                        View in DonorAtlas
                        <ng-container *ngTemplateOutlet="externalLinkIcon"></ng-container>
                    </a>
                </div>
            </div>

            <!-- Processing: spinner, verb pill, determinate bar when the
                 job reports progress/total. -->
            <div *ngIf="summary.processing && !isOrgPage()" class="da-state">
                <app-spinning-logo [size]="28"></app-spinning-logo>
                <div class="da-pill da-pill-processing">
                    <span class="da-dot"></span>{{ processingVerbLabel }}
                </div>
                <!-- Bar only, no "n of m": a single constituent's job counts
                     internal item writes, which mean nothing to a user. -->
                <div *ngIf="processingPercent !== null" class="da-progress">
                    <div class="da-progress-fill" [style.width.%]="processingPercent"></div>
                </div>
            </div>

            <!-- One centered stack: mark · status · one-liner · primary
                 action, then the manual-match picker beneath a divider. -->
            <div *ngIf="!summary.processing && !isOrgPage() && (isNotSyncedYet() || isNoMatchFound())"
                class="da-panel">
                <div class="da-state">
                    <ng-container *ngTemplateOutlet="daMark"></ng-container>
                    <div *ngIf="isNotSyncedYet()" class="da-pill"><span
                            class="da-dot da-dot-neutral"></span>Not synced</div>
                    <div *ngIf="isNoMatchFound()" class="da-pill da-pill-warn"><span
                            class="da-dot da-dot-warn"></span>No match found</div>
                    <p *ngIf="isNoMatchFound() && syncStamp" class="da-state-stamp">{{ syncStamp }}</p>
                    <p class="da-state-sub">
                        <ng-container *ngIf="isNotSyncedYet()">Sync this constituent to a DonorAtlas
                            profile to see wealth and giving insights.</ng-container>
                        <ng-container *ngIf="isNoMatchFound()">No DonorAtlas profile matched. Update
                            their details in RE NXT and try again.</ng-container>
                    </p>

                    <!-- Without the sync permission there is nothing to offer
                         here but the reason why (and Unsync on a no-match row,
                         below, when that alone is granted). -->
                    <p *ngIf="!canSync" class="da-hint">{{ permissionHint }}</p>

                    <!-- Under a spent daily quota the whole action set folds
                         into one line: nothing here can run for hours, and a
                         wall of disabled controls reads worse than one
                         sentence saying when to come back. -->
                    <p *ngIf="quotaExhausted" class="da-hint" [title]="quotaTitle">{{ quotaTitle }}</p>

                    <button *ngIf="!quotaExhausted && canSync && isNotSyncedYet()" class="da-btn da-btn-primary" type="button"
                        (click)="onSyncClick()" [disabled]="mutationInFlight">
                        {{ enhancing ? 'Starting sync…' : 'Sync with DonorAtlas' }}
                    </button>

                    <!-- "Try match again" re-runs the match (the backend clears
                         the no-match row and starts a fresh machine sync; uses
                         1 import); Unsync just clears the record. -->
                    <div *ngIf="!quotaExhausted && isNoMatchFound() && (canSync || canUnsync)" class="da-actions">
                        <button *ngIf="canSync" class="da-btn da-btn-secondary" type="button"
                            (click)="onResyncClick()" [disabled]="mutationInFlight">
                            <ng-container *ngTemplateOutlet="resyncIcon"></ng-container>
                            {{ enhancing ? 'Retrying…' : 'Try match again' }}
                        </button>
                        <button *ngIf="canUnsync" class="da-btn da-btn-ghost" type="button" (click)="onUnsyncClick()"
                            [disabled]="mutationInFlight">
                            {{ unsyncing ? 'Unsyncing…' : 'Unsync' }}
                        </button>
                    </div>
                </div>

                <!-- Manual match. One input serves a name
                     search and a pasted profile URL; confirming links the
                     chosen donor and starts the same write-back job flow as
                     sync (uses 1 import). The well only appears once there is
                     a search to show, and then keeps a fixed height so
                     results, the confirm card, and hints swap without
                     resizing the tile. -->
                <div *ngIf="canSync && !quotaExhausted" class="da-match">
                    <div class="da-match-label"><span>or match manually</span></div>
                    <div class="da-input-wrap">
                        <svg class="da-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input class="da-input" type="text"
                            placeholder="Search donors or paste a profile URL" [value]="matchQuery"
                            (input)="onMatchQueryChange($any($event.target).value)"
                            [disabled]="matchSubmitting" />
                    </div>

                    <div *ngIf="matchQuery.trim().length >= 2 || matchSelection || matchError"
                        class="da-match-well">
                        <div *ngIf="matchSearchLoading" class="da-well-empty">Searching…</div>
                        <div *ngIf="!matchSearchLoading && !matchError && matchQuery.trim().length >= 2 && matchResults.length === 0 && !matchSelection"
                            class="da-well-empty">No donors found.</div>

                        <!-- Each row is a real button (Enter and Space select it)
                             with the profile link beside it, not inside it. -->
                        <ul *ngIf="matchResults.length > 0 && !matchSelection && !matchSearchLoading"
                            class="da-results">
                            <li *ngFor="let result of matchResults" class="da-row">
                                <button class="da-donor" type="button" (click)="onMatchSelect(result)">
                                    <ng-container
                                        *ngTemplateOutlet="donorIdentity; context: { $implicit: result }"></ng-container>
                                </button>
                                <a *ngIf="result.profile_url" class="da-result-link" [href]="result.profile_url"
                                    target="_blank" rel="noopener noreferrer" title="Open in DonorAtlas">
                                    <ng-container *ngTemplateOutlet="externalLinkIcon"></ng-container>
                                </a>
                            </li>
                        </ul>

                        <div *ngIf="matchSelection" class="da-confirm">
                            <div class="da-row">
                                <div class="da-donor">
                                    <ng-container
                                        *ngTemplateOutlet="donorIdentity; context: { $implicit: matchSelection }"></ng-container>
                                </div>
                                <a *ngIf="matchSelection.profile_url" class="da-result-link"
                                    [href]="matchSelection.profile_url" target="_blank"
                                    rel="noopener noreferrer" title="Open in DonorAtlas">
                                    <ng-container *ngTemplateOutlet="externalLinkIcon"></ng-container>
                                </a>
                            </div>
                            <div class="da-actions">
                                <button class="da-btn da-btn-primary" type="button"
                                    (click)="onMatchConfirm()" [disabled]="matchSubmitting">
                                    {{ matchSubmitting ? 'Matching…' : 'Confirm match' }}
                                </button>
                                <button class="da-btn da-btn-ghost" type="button"
                                    (click)="onMatchClearSelection()" [disabled]="matchSubmitting">
                                    Back
                                </button>
                            </div>
                        </div>

                        <div *ngIf="matchError" class="da-hint da-hint-error">{{ matchError }}</div>
                    </div>
                </div>
            </div>
        </div>
    </ng-container>
</div>
